import type { WorkspaceProjection } from '@localagent/sdk';
import { canMoveWorkspaceHere, WorkspaceMoveControl } from './WorkspaceMoveControl';
import { Badge } from '@localagent/ui/components/badge';
import { Tooltip } from '@localagent/ui/components/tooltip';
import { cn } from '@localagent/ui/lib/utils';
import {
  displayValue,
  filesystemStatusLabel,
  filesystemTone,
  workstationScreenCaption,
  workstationStatusLabel,
  workstationTone,
  type ResourceTone,
} from './workspaceDisplay';

const TONE_LED: Record<ResourceTone, string> = {
  ok: 'bg-emerald-400 shadow-[0_0_8px_rgba(52,211,153,0.85)]',
  busy: 'bg-amber-400 shadow-[0_0_8px_rgba(251,191,36,0.75)] motion-safe:animate-pulse',
  bad: 'bg-red-500 shadow-[0_0_8px_rgba(239,68,68,0.85)]',
  off: 'bg-zinc-500/50',
};

const TONE_GLOW: Record<ResourceTone, string> = {
  ok: 'shadow-[0_0_24px_rgba(16,185,129,0.18)]',
  busy: 'shadow-[0_0_24px_rgba(245,158,11,0.16)]',
  bad: 'shadow-[0_0_24px_rgba(239,68,68,0.16)]',
  off: '',
};

const TONE_BADGE: Record<ResourceTone, 'success' | 'warning' | 'destructive' | 'secondary'> = {
  ok: 'success',
  busy: 'warning',
  bad: 'destructive',
  off: 'secondary',
};

function workstationHeadline(projection: WorkspaceProjection): string {
  const { kind, status } = projection.workstation;
  if (kind === 'local') {
    switch (status) {
      case 'connected':
        return 'This computer is attached and ready for the agent.';
      case 'disconnected':
        return 'The assigned computer is offline. Open the desktop app to attach it.';
      case 'error':
      case 'failed':
        return 'This computer reported a failure.';
      case 'retired':
        return 'This computer is no longer assigned.';
      default:
        return `This computer is ${workstationStatusLabel(status)}.`;
    }
  }
  switch (status) {
    case 'running':
      return 'The cloud computer is powered on. The agent can execute here.';
    case 'sleeping':
      return 'The cloud computer is asleep. Wake it to run the agent.';
    case 'starting':
      return 'The cloud computer is waking.';
    case 'pending':
      return 'The cloud computer has not started yet.';
    case 'reclaiming':
      return 'The cloud computer is shutting down and will be reclaimed.';
    case 'error':
    case 'failed':
      return 'The cloud computer failed.';
    case 'retired':
      return 'The cloud computer is no longer assigned.';
    case 'connected':
    case 'disconnected':
      return `The cloud computer is ${workstationStatusLabel(status)}.`;
  }
}

function filesystemHeadline(projection: WorkspaceProjection): string {
  if (projection.binding?.state === 'missing') {
    return 'The bound folder is gone from this computer.';
  }
  switch (projection.filesystem.state) {
    case 'ready':
      return projection.filesystem.kind === 'local'
        ? 'A folder on this computer holds the files the agent can use.'
        : 'A durable cloud volume holds the files the agent can use.';
    case 'provisioning':
      return 'The volume is being prepared. Files are not ready yet.';
    case 'error':
      return 'This volume failed and cannot serve files.';
    case 'retired':
      return 'This volume is no longer assigned.';
  }
}

function LaptopGlyph({
  tone,
  lit,
  caption,
}: {
  tone: ResourceTone;
  lit: boolean;
  caption: string | null;
}) {
  return (
    <div className="flex w-[9.75rem] flex-col items-center" data-testid="workspace-workstation-glyph">
      <div
        className={cn(
          'w-full rounded-t-xl border-2 border-b-0 border-zinc-400 bg-zinc-300 p-1.5 dark:border-zinc-600 dark:bg-zinc-800',
          TONE_GLOW[tone]
        )}
      >
        <div
          className={cn(
            'relative flex h-[4.75rem] items-center justify-center overflow-hidden rounded-md',
            lit
              ? 'bg-gradient-to-b from-emerald-950 via-slate-950 to-slate-950'
              : 'bg-zinc-950'
          )}
        >
          {lit ? (
            <>
              <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(rgba(52,211,153,0.08)_1px,transparent_1px)] bg-[size:100%_7px]" />
              <div className="relative font-mono text-[10px] leading-4 text-emerald-300/90">
                <div>agent@workstation</div>
                <div>
                  $<span className="motion-safe:animate-pulse">▌</span>
                </div>
              </div>
            </>
          ) : (
            <span className="font-mono text-[9px] tracking-[0.2em] text-zinc-500">{caption}</span>
          )}
        </div>
      </div>
      <div className="relative h-3 w-[10.5rem] rounded-b-md bg-zinc-400 dark:bg-zinc-700">
        <div className="absolute inset-x-10 top-1 h-1 rounded-full bg-zinc-300/80 dark:bg-zinc-500/70" />
        <span className={cn('absolute right-2 top-1 h-1.5 w-1.5 rounded-full', TONE_LED[tone])} />
      </div>
    </div>
  );
}

function CloudComputerGlyph({
  tone,
  lit,
  caption,
}: {
  tone: ResourceTone;
  lit: boolean;
  caption: string | null;
}) {
  return (
    <div className="flex items-end gap-3" data-testid="workspace-workstation-glyph">
      <div className="flex flex-col items-center">
        <div
          className={cn(
            'rounded-lg border-2 border-zinc-400 bg-zinc-300 p-1.5 dark:border-zinc-600 dark:bg-zinc-800',
            TONE_GLOW[tone]
          )}
        >
          <div
            className={cn(
              'relative flex h-16 w-[6.5rem] items-center justify-center overflow-hidden rounded-sm',
              lit ? 'bg-slate-950' : 'bg-zinc-950'
            )}
          >
            {lit ? (
              <div className="grid grid-cols-6 gap-0.5 p-2">
                {Array.from({ length: 18 }, (_, index) => (
                  <span
                    key={index}
                    className={cn(
                      'h-1.5 w-1.5 rounded-[1px]',
                      tone === 'busy' ? 'bg-amber-300/70' : 'bg-sky-300/70',
                      index % 3 === 0 && 'opacity-40'
                    )}
                  />
                ))}
              </div>
            ) : (
              <span className="font-mono text-[8px] tracking-[0.18em] text-zinc-500">{caption}</span>
            )}
          </div>
        </div>
        <div className="h-2 w-4 bg-zinc-400 dark:bg-zinc-600" />
        <div className="h-1.5 w-14 rounded-sm bg-zinc-400 dark:bg-zinc-600" />
      </div>
      <div className="flex h-[5.5rem] w-11 flex-col justify-between rounded-md border-2 border-zinc-400 bg-zinc-300 p-1.5 dark:border-zinc-600 dark:bg-zinc-800">
        <div className="space-y-1">
          <div className="h-1.5 rounded-sm bg-zinc-500/50 dark:bg-zinc-500/70" />
          <div className="h-1.5 rounded-sm bg-zinc-500/50 dark:bg-zinc-500/70" />
          <div className="h-1.5 rounded-sm bg-zinc-500/40 dark:bg-zinc-500/60" />
        </div>
        <span className={cn('h-1.5 w-1.5 self-end rounded-full', TONE_LED[tone])} />
      </div>
    </div>
  );
}

function FolderGlyph({ tone, ready }: { tone: ResourceTone; ready: boolean }) {
  return (
    <div className="relative w-[8.5rem]" data-testid="workspace-filesystem-glyph">
      <div className="absolute left-3 top-0 h-3 w-12 rounded-t-md bg-amber-500/90 dark:bg-amber-600/80" />
      <div
        className={cn(
          'mt-2 flex h-[5.25rem] flex-col justify-end rounded-md border-2 p-2.5',
          ready
            ? 'border-amber-600/70 bg-gradient-to-b from-amber-100 to-amber-200 dark:border-amber-500/40 dark:from-amber-900/70 dark:to-amber-950'
            : 'border-zinc-400 bg-zinc-200 dark:border-zinc-600 dark:bg-zinc-800',
          TONE_GLOW[tone]
        )}
      >
        <div className="space-y-1.5">
          <div className={cn('h-1.5 rounded-full', ready ? 'w-4/5 bg-amber-800/25 dark:bg-amber-200/25' : 'w-3/5 bg-zinc-400/50')} />
          <div className={cn('h-1.5 rounded-full', ready ? 'w-3/5 bg-amber-800/20 dark:bg-amber-200/20' : 'w-2/5 bg-zinc-400/40')} />
          <div className={cn('h-1.5 rounded-full', ready ? 'w-2/3 bg-amber-800/15 dark:bg-amber-200/15' : 'w-1/2 bg-zinc-400/30')} />
        </div>
      </div>
      <span className={cn('absolute bottom-2 right-2 h-2 w-2 rounded-full', TONE_LED[tone])} />
    </div>
  );
}

function VolumeGlyph({ tone, ready }: { tone: ResourceTone; ready: boolean }) {
  return (
    <div className="flex w-[8.5rem] flex-col items-center" data-testid="workspace-filesystem-glyph">
      <div
        className={cn(
          'relative flex h-[6.25rem] w-[6.25rem] items-center justify-center rounded-2xl border-2',
          ready
            ? 'border-sky-500/50 bg-gradient-to-br from-sky-100 to-slate-200 dark:from-sky-950 dark:to-slate-900'
            : 'border-zinc-400 bg-zinc-200 dark:border-zinc-600 dark:bg-zinc-800',
          TONE_GLOW[tone]
        )}
      >
        <div
          className={cn(
            'flex h-16 w-16 items-center justify-center rounded-full border-[6px]',
            ready
              ? 'border-sky-400/70 dark:border-sky-500/50'
              : 'border-zinc-400/70 dark:border-zinc-500/50',
            tone === 'busy' && 'motion-safe:animate-spin'
          )}
          style={tone === 'busy' ? { animationDuration: '3s' } : undefined}
        >
          <div
            className={cn(
              'h-5 w-5 rounded-full',
              ready ? 'bg-sky-500/80 dark:bg-sky-400/70' : 'bg-zinc-400 dark:bg-zinc-500'
            )}
          />
        </div>
        <span className={cn('absolute bottom-2 right-2 h-2 w-2 rounded-full', TONE_LED[tone])} />
      </div>
    </div>
  );
}

function workstationScreenLit(status: WorkspaceProjection['workstation']['status']): boolean {
  return status === 'connected' || status === 'running' || status === 'starting';
}

function filesystemReady(projection: WorkspaceProjection): boolean {
  return projection.filesystem.state === 'ready' && projection.binding?.state !== 'missing';
}

export function WorkspaceWorkstationCard({ projection }: { projection: WorkspaceProjection }) {
  const tone = workstationTone(projection.workstation.status);
  const lit = workstationScreenLit(projection.workstation.status);
  const caption = workstationScreenCaption(
    projection.workstation.status,
    projection.workstation.kind
  );
  const kindLabel = projection.workstation.kind === 'local' ? 'This computer' : 'Cloud computer';

  return (
    <article
      className="flex flex-col rounded-lg bg-card p-4"
      data-testid="workspace-workstation-card"
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <h4 className="font-semibold">Workstation</h4>
          <p className="mt-0.5 text-xs text-muted-foreground">The machine the agent runs on</p>
        </div>
        <Badge variant={TONE_BADGE[tone]}>{workstationStatusLabel(projection.workstation.status)}</Badge>
      </div>
      <div className="mt-4 flex min-h-[7.5rem] items-center justify-center">
        {projection.workstation.kind === 'local' ? (
          <LaptopGlyph tone={tone} lit={lit} caption={caption} />
        ) : (
          <CloudComputerGlyph tone={tone} lit={lit} caption={caption} />
        )}
      </div>
      <p className="mt-4 text-sm">{workstationHeadline(projection)}</p>
      <dl className="mt-3 space-y-1 text-xs text-muted-foreground">
        <div className="flex justify-between gap-3">
          <dt>Kind</dt>
          <dd className="font-medium text-foreground">{kindLabel}</dd>
        </div>
        {projection.workstation.device_id && (
          <div className="flex justify-between gap-3">
            <dt>Assigned device</dt>
            <dd className="truncate font-mono text-[11px] text-foreground">
              {projection.workstation.device_id}
            </dd>
          </div>
        )}
        <div className="flex justify-between gap-3">
          <dt>Agent execution</dt>
          <dd className="font-medium text-foreground">
            {projection.capabilities.execution ? 'Available' : 'Unavailable'}
          </dd>
        </div>
      </dl>
      {projection.workstation.environment_unsaved && (
        <p className="mt-3 rounded-md bg-amber-500/10 px-2 py-1.5 text-xs">
          Runtime changes on this computer are not saved yet.
        </p>
      )}
      {projection.workstation.failure_reason && (
        <p className="mt-3 text-sm text-destructive">{projection.workstation.failure_reason}</p>
      )}
      {canMoveWorkspaceHere(projection) && <WorkspaceMoveControl projection={projection} />}
    </article>
  );
}

export function WorkspaceFilesystemCard({ projection }: { projection: WorkspaceProjection }) {
  const tone = filesystemTone(projection.filesystem.state, projection.binding?.state);
  const ready = filesystemReady(projection);
  const kindLabel = projection.filesystem.kind === 'local' ? 'Folder on this computer' : 'Cloud volume';

  return (
    <article
      className="flex flex-col rounded-lg bg-card p-4"
      data-testid="workspace-filesystem-card"
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <h4 className="font-semibold">Filesystem</h4>
          <p className="mt-0.5 text-xs text-muted-foreground">The files the agent can use</p>
        </div>
        <Badge variant={TONE_BADGE[tone]}>{filesystemStatusLabel(projection)}</Badge>
      </div>
      <div className="mt-4 flex min-h-[7.5rem] items-center justify-center">
        {projection.filesystem.kind === 'local' ? (
          <FolderGlyph tone={tone} ready={ready} />
        ) : (
          <VolumeGlyph tone={tone} ready={ready} />
        )}
      </div>
      <p className="mt-4 text-sm">{filesystemHeadline(projection)}</p>
      <dl className="mt-3 space-y-1 text-xs text-muted-foreground">
        <div className="flex justify-between gap-3">
          <dt>Kind</dt>
          <dd className="font-medium text-foreground">{kindLabel}</dd>
        </div>
        <div className="flex justify-between gap-3">
          <dt>Read</dt>
          <dd className="font-medium text-foreground">
            {projection.capabilities.content_readable ? 'Mounted' : 'Unavailable'}
          </dd>
        </div>
        <div className="flex justify-between gap-3">
          <dt>Write</dt>
          <dd className="font-medium text-foreground">
            {projection.capabilities.content_writable ? 'Mounted' : 'Unavailable'}
          </dd>
        </div>
      </dl>
      {projection.binding && (
        <div className="mt-3 rounded-md bg-muted px-2 py-1.5 text-xs" data-testid="workspace-binding">
          <p className="font-medium text-foreground">
            {projection.binding.folder_path} · {displayValue(projection.binding.state)}
          </p>
        </div>
      )}
      {projection.filesystem.failure_reason && (
        <p className="mt-3 text-sm text-destructive">{projection.filesystem.failure_reason}</p>
      )}
    </article>
  );
}

export function WorkspaceResourceCards({ projection }: { projection: WorkspaceProjection }) {
  return (
    <div className="mt-4 grid gap-3 lg:grid-cols-2" data-testid="workspace-resource-cards">
      <WorkspaceWorkstationCard projection={projection} />
      <WorkspaceFilesystemCard projection={projection} />
    </div>
  );
}

function MiniLaptop({ tone }: { tone: ResourceTone }) {
  return (
    <span className="relative inline-flex h-4 w-6 items-end" aria-hidden>
      <span className="absolute inset-x-0.5 top-0 h-3 rounded-[2px] border border-zinc-400 bg-zinc-700 dark:border-zinc-500" />
      <span className="h-0.5 w-full rounded-sm bg-zinc-400 dark:bg-zinc-500" />
      <span className={cn('absolute right-0.5 top-0.5 h-1 w-1 rounded-full', TONE_LED[tone])} />
    </span>
  );
}

function MiniTower({ tone }: { tone: ResourceTone }) {
  return (
    <span className="relative inline-flex h-4 w-3" aria-hidden>
      <span className="h-full w-full rounded-[2px] border border-zinc-400 bg-zinc-300 dark:border-zinc-500 dark:bg-zinc-700" />
      <span className={cn('absolute bottom-0.5 right-0.5 h-1 w-1 rounded-full', TONE_LED[tone])} />
    </span>
  );
}

function MiniFolder({ tone }: { tone: ResourceTone }) {
  return (
    <span className="relative inline-flex h-4 w-5 items-end" aria-hidden>
      <span className="absolute left-0.5 top-0 h-1.5 w-2 rounded-t-[2px] bg-amber-500/90" />
      <span className="h-3 w-full rounded-[2px] border border-amber-700/40 bg-amber-200 dark:border-amber-500/40 dark:bg-amber-900" />
      <span className={cn('absolute bottom-0.5 right-0.5 h-1 w-1 rounded-full', TONE_LED[tone])} />
    </span>
  );
}

function MiniVolume({ tone }: { tone: ResourceTone }) {
  return (
    <span className="relative inline-flex h-4 w-4 items-center justify-center" aria-hidden>
      <span className="h-3.5 w-3.5 rounded-full border-2 border-sky-500/70 dark:border-sky-400/60" />
      <span className={cn('absolute h-1.5 w-1.5 rounded-full', TONE_LED[tone])} />
    </span>
  );
}

export function WorkspaceResourceSummary({
  projection,
  className,
}: {
  projection: WorkspaceProjection;
  className?: string;
}) {
  const machineTone = workstationTone(projection.workstation.status);
  const volumeTone = filesystemTone(projection.filesystem.state, projection.binding?.state);

  return (
    <div
      className={cn('flex flex-wrap items-center gap-3 text-xs', className)}
      data-testid="workspace-resource-summary"
    >
      <span className="inline-flex items-center gap-1.5" data-testid="workspace-workstation-summary">
        {projection.workstation.kind === 'local' ? (
          <MiniLaptop tone={machineTone} />
        ) : (
          <MiniTower tone={machineTone} />
        )}
        <span className="text-muted-foreground">
          {projection.workstation.kind === 'local' ? 'Computer' : 'Cloud computer'}
        </span>
        <span className="font-medium">{workstationStatusLabel(projection.workstation.status)}</span>
      </span>
      <span className="inline-flex items-center gap-1.5" data-testid="workspace-filesystem-summary">
        {projection.filesystem.kind === 'local' ? (
          <MiniFolder tone={volumeTone} />
        ) : (
          <MiniVolume tone={volumeTone} />
        )}
        <span className="text-muted-foreground">
          {projection.filesystem.kind === 'local' ? 'Folder' : 'Cloud volume'}
        </span>
        <span className="font-medium">{filesystemStatusLabel(projection)}</span>
      </span>
    </div>
  );
}

function MiniIndex({ tone }: { tone: ResourceTone }) {
  return (
    <span className="relative inline-flex h-4 w-4 items-center justify-center" aria-hidden>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.25"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="text-zinc-400 dark:text-zinc-500"
      >
        <circle cx="10.5" cy="10.5" r="6.5" />
        <path d="m20 20-4.5-4.5" />
      </svg>
      <span className={cn('absolute left-[5px] top-[5px] h-1.5 w-1.5 rounded-full', TONE_LED[tone])} />
    </span>
  );
}

function indexTone(index: WorkspaceProjection['index']): ResourceTone {
  if (index.health !== 'ok') return 'bad';
  return index.progress === 'indexing' ? 'busy' : 'ok';
}

function indexDetail(index: WorkspaceProjection['index']): string {
  const counts = `${index.indexed} indexed, ${index.queued} queued, ${index.embedding} embedding`;
  if (index.health !== 'ok') return `Search index ${displayValue(index.health)} — ${counts}`;
  if (index.progress === 'indexing') return `Search index updating — ${counts}`;
  return `Search index up to date — ${index.indexed} indexed`;
}

function withFailure(detail: string, failure: string | null | undefined): string {
  return failure ? `${detail} ${failure}` : detail;
}

export function WorkspaceStatusIcons({
  projection,
  showIndex,
}: {
  projection: WorkspaceProjection;
  showIndex: boolean;
}) {
  const local = projection.workstation.kind === 'local';
  const localFolder = projection.filesystem.kind === 'local';
  const machineTone = workstationTone(projection.workstation.status);
  const volumeTone = filesystemTone(projection.filesystem.state, projection.binding?.state);
  const machineDetail = withFailure(
    `${local ? 'Computer' : 'Cloud computer'} ${workstationStatusLabel(projection.workstation.status)} — ${workstationHeadline(projection)}`,
    projection.workstation.failure_reason,
  );
  const volumeDetail = withFailure(
    `${localFolder ? 'Folder' : 'Cloud volume'} ${filesystemStatusLabel(projection)} — ${filesystemHeadline(projection)}`,
    projection.filesystem.failure_reason,
  );

  return (
    <span className="inline-flex items-center gap-1.5" data-testid="workspace-status-icons">
      <Tooltip label={machineDetail}>
        <span
          tabIndex={0}
          data-testid="workspace-status-workstation"
          data-tone={machineTone}
          className="inline-flex rounded p-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          {local ? <MiniLaptop tone={machineTone} /> : <MiniTower tone={machineTone} />}
        </span>
      </Tooltip>
      <Tooltip label={volumeDetail}>
        <span
          tabIndex={0}
          data-testid="workspace-status-filesystem"
          data-tone={volumeTone}
          className="inline-flex rounded p-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          {localFolder ? <MiniFolder tone={volumeTone} /> : <MiniVolume tone={volumeTone} />}
        </span>
      </Tooltip>
      {showIndex && (
        <Tooltip label={indexDetail(projection.index)}>
          <span
            tabIndex={0}
            data-testid="workspace-status-index"
            data-tone={indexTone(projection.index)}
            className="inline-flex rounded p-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <MiniIndex tone={indexTone(projection.index)} />
          </span>
        </Tooltip>
      )}
    </span>
  );
}
